import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from 'react';
import { ProjectsReportRow } from '../../../utils/api/report';
import { Terms, termsFor } from '@stormwater-software/lib-domain';

import { useTerminology } from '../../../hooks/useTerminology';
export interface ColumnDef {
  id: number;
  label: string;
  value: string;
  isEnable: boolean;
  showInOptions: boolean;
  numeric?: boolean;
  width?: string;
}

export const getStaticColumns = (terms: Terms): ColumnDef[] => [
  { id: 0,  label: 'Region Name',            value: 'regionName',         isEnable: false, showInOptions: true, width: '6%' },
  { id: 1,  label: 'Division Name',          value: 'divisionName',       isEnable: true,  showInOptions: true, width: '8%' },
  { id: 2,  label: 'Area',                   value: 'areaName',           isEnable: false, showInOptions: true, width: '7%' },
  { id: 3,  label: 'Project Name',           value: 'projectName',        isEnable: true,  showInOptions: true, width: '10%' },
  { id: 4,  label: `${terms.entity} Name`,         value: 'clientName',         isEnable: true,  showInOptions: true, width: '10%' },
  { id: 5,  label: terms.projectPermittee,   value: 'permittee',          isEnable: true,  showInOptions: true, width: '10%' },
  { id: 6,  label: 'Project Type',           value: 'projectType',        isEnable: true,  showInOptions: true, width: '7%' },
  { id: 7,  label: 'Inspector Name',         value: 'inspectorName',      isEnable: true,  showInOptions: true, width: '8%' },
  { id: 8,  label: 'Inspection Interval',    value: 'inspectionInterval', isEnable: true,  showInOptions: true, width: '8%' },
  { id: 9,  label: 'Lat / Long',             value: 'latLong',            isEnable: false, showInOptions: true, width: '8%' },
  { id: 10, label: 'Permit Number',          value: 'permitNumber',       isEnable: true,  showInOptions: true, width: '8%' },
  { id: 11, label: 'Acreage',                value: 'currentAcreage',     isEnable: false, showInOptions: true, width: '5%', numeric: true },
  { id: 12, label: 'Start Date',             value: 'startDate',          isEnable: false, showInOptions: true, width: '6%' },
  { id: 13, label: 'End Date',               value: 'endDate',            isEnable: false, showInOptions: true, width: '6%' },
  { id: 14, label: 'Project Status',         value: 'status',             isEnable: true,  showInOptions: true, width: '5%' },
  { id: 15, label: 'Work Order',             value: 'workOrder',          isEnable: false, showInOptions: true, width: '7%' },
  { id: 16, label: 'Purchase Order',         value: 'purchaseOrder',      isEnable: false, showInOptions: true, width: '7%' },
  { id: 17, label: 'Project Config',         value: 'projectConfig',      isEnable: false, showInOptions: true, width: '6%' },
];

export interface IProjectsReportContext {
  data: ProjectsReportRow[];
  setData: Dispatch<SetStateAction<ProjectsReportRow[]>>;
  columns: ColumnDef[];
  setColumns: Dispatch<SetStateAction<ColumnDef[]>>;
  formData: any;
  setFormData: Dispatch<SetStateAction<any>>;
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  isModify: boolean;
  setIsModify: Dispatch<SetStateAction<boolean>>;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const throwError = () => {
  throw new Error('ProjectsReportProvider not loaded');
};

const ProjectsReportContext = createContext<IProjectsReportContext>({
  data: [],
  setData: throwError,
  columns: getStaticColumns(termsFor()),
  setColumns: throwError,
  formData: {},
  setFormData: throwError,
  showModal: false,
  setShowModal: throwError,
  isModify: false,
  setIsModify: throwError,
  open: false,
  setOpen: throwError,
});

export const ProjectsReportProvider: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const terms = useTerminology();
  const [data, setData] = useState<ProjectsReportRow[]>([]);
  const [columns, setColumns] = useState<ColumnDef[]>(() => getStaticColumns(terms));
  const [formData, setFormData] = useState<any>({ regions: [] });
  const [showModal, setShowModal] = useState(true);
  const [isModify, setIsModify] = useState(false);
  const [open, setOpen] = useState(false);

  return (
    <ProjectsReportContext.Provider
      value={{
        data,
        setData,
        columns,
        setColumns,
        formData,
        setFormData,
        showModal,
        setShowModal,
        isModify,
        setIsModify,
        open,
        setOpen,
      }}
    >
      {children}
    </ProjectsReportContext.Provider>
  );
};

export const useProjectsReport = () => useContext(ProjectsReportContext);

export default ProjectsReportContext;
